import { useCallback, useEffect, useState } from "react";
import { getPanelDataset } from "../../../services/adminPanels";
import { statusFromError } from "../../../components/charts/panelStatus";

/**
 * One dataset's rows for a range (ASB-4377). Reloads when the panel, dataset,
 * range, region, drill filters or table query (A.6: q, sort, dir, limit,
 * offset) change; `reload()` retries after a failure. The status is the
 * card's: loading, ready, empty (the API answered with no rows), or missing /
 * forbidden / failed from the error (see panelStatus.js).
 *
 * With `keepData`, a re-read keeps the rows it already has while loading
 * (`stale: true`), so a searchable table's box and pager stay put while the
 * next page arrives instead of unmounting into a spinner.
 *
 * `version` is the panel's `computedAt`: when the DAG writes a new run, the
 * rows are re-read for the same range, so a page left open past the morning
 * run shows the day it now has, not the empty answer it got before.
 */
export function usePanelDataset(
	panelId,
	datasetId,
	{ from, to, region, filters, q, sort, dir, limit, offset, keepData, version } = {}
) {
	const filterKey = JSON.stringify(filters ?? {});
	const queryKey = JSON.stringify({ q: q ?? "", sort: sort ?? null, dir: dir ?? null, limit, offset });
	const [state, setState] = useState({ status: "loading", data: null, error: null, stale: false });
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		let cancelled = false;
		setState((s) => ({
			status: "loading",
			data: keepData && s.data ? s.data : null,
			error: null,
			stale: Boolean(keepData && s.data),
		}));
		const query = JSON.parse(queryKey);
		getPanelDataset(panelId, datasetId, {
			from,
			to,
			region,
			filters: JSON.parse(filterKey),
			q: query.q || undefined,
			sort: query.sort ?? undefined,
			dir: query.dir ?? undefined,
			limit: query.limit,
			offset: query.offset,
		})
			.then((body) => {
				if (cancelled) return;
				const data = body && typeof body.data === "object" && body.data ? body.data : {};
				const rows = Array.isArray(data.rows) ? data.rows : [];
				setState({
					status: rows.length ? "ready" : "empty",
					data: { ...data, rows },
					error: null,
					stale: false,
				});
			})
			.catch((error) => {
				if (cancelled) return;
				console.error(`Admin panel dataset ${panelId}/${datasetId} unavailable`, error);
				setState({ status: statusFromError(error), data: null, error, stale: false });
			});
		return () => {
			cancelled = true;
		};
	}, [panelId, datasetId, from, to, region, filterKey, queryKey, keepData, version, attempt]);

	const reload = useCallback(() => setAttempt((n) => n + 1), []);
	return { ...state, reload };
}
